import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, withDirectives as _withDirectives, withCtx as _withCtx, withModifiers as _withModifiers, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "dialog-box dialog-box-restore" }
const _hoisted_2 = { class: "dialog" }
const _hoisted_3 = { class: "dialog__links" }
const _hoisted_4 = { class: "dialog__links" }
const _hoisted_5 = { class: "dialog-head" }
const _hoisted_6 = { class: "dialog__text" }
const _hoisted_7 = { class: "dialog__form-pin" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "timer" }
const _hoisted_10 = { class: "dialog__links" }
const _hoisted_11 = { class: "password-error" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = {
  key: 0,
  class: "dialog__links"
}

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { vMaska } from "maska/vue"
import { useStore } from 'vuex';
import router from '@/router';
import VitaCodeInput from '@/components/UI/VitaCodeInput.vue';
import VitaPasswordValidator from '@/components/UI/VitaPasswordValidator.vue';
import { ResetPassword, StartResetPassword } from '@/store/user/types';
import { useTimer } from '@/utils/timer';
import { userLogout } from '@/utils/user';
import { reachGoal } from '@/utils/metrika';

interface Props {
  softReset?: boolean,
}
const timerInterval = 0;

// Модальные окна

export default /*@__PURE__*/_defineComponent({
  __name: 'AuthenticationRestoreDialog',
  props: {
    softReset: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const store = useStore()
const { timeLeft, startTimer } = useTimer(60);

const error = computed(() => {
  return store.getters['user/loginError']
})

// Шаги восстановления пароля
const step = computed(() => {
  return store.getters['user/getPasswordResetStep']
})

const inProcess = ref(false);
const email = ref("");
const snils = ref("");
const phone = ref("");
const phoneInput = ref<HTMLInputElement | null>(null);
const code = ref('');
const newPassword = ref("");
const newPasswordRetype = ref("");
const newPasswordsValid = ref(false);

// Таймер для отправки кода
const openSuccessModal = ref(false)
const modalTitle = ref('Пароль успешно изменен')

const emailValid = ref(false);
const snilsValid = ref(false);
const phoneValid = ref(false);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const snilsRegex = /^\d{3}-\d{3}-\d{3} \d{2}$/;
const phoneRegex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
const spaceKeyRegex = /\s/;

const pinInputRef = ref<InstanceType<typeof VitaCodeInput> | null>(null);
// Проверки для инпутов
const validateEmail = () => {
  emailValid.value = emailRegex.test(email.value);
};

const validateSnils = () => {
  snilsValid.value = snilsRegex.test(snils.value);
};

const validatePhone = () => {
  phoneValid.value = phoneRegex.test(phone.value);
};

// Проверка на заполненность для Email и СНИЛС
const isFormValid = computed(() => emailValid.value && snilsValid.value);

const onBackLinkClicked = () => {
  // Переходим на шаг назад
  store.dispatch('user/updatePasswordResetStep', step.value - 1)

  // Выход с первого шага на главную
  if (step.value === 0) router.push('/');

  // Убираем UID в sessionStorage в зависимости от шага
  if (step.value === 1) sessionStorage.removeItem('resetSessionUID')
  code.value = ''
}

// Получение пин-кода из инпутов
const handlePinInput = (pin: number | null) => {
  code.value = pin ? pin.toString() : ''
}

// Форматированный текст таймера
const formattedTime = computed<string>(() => {
  const minutes = Math.floor(timeLeft.value / 60);
  const seconds = timeLeft.value % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const focusPinInput = () => {
  pinInputRef.value?.focusFirstInput();
};

// Функция для нового запроса
const sendNewCode = () => {
  const phoneValue = phone.value.replace(/[^+\d]/g, '');
  executeAction<string>('user/phoneInput', phoneValue);
  focusPinInput()
  startTimer()
};

// Проверка ввода нового пароля
const passwordCheck = (value: boolean) => {
  newPasswordsValid.value = value;
}

const newPasswordPairValid = (): boolean => {
  if (spaceKeyRegex.test(newPassword.value)) return false // Проверка на наличие пробела в пароле
  return newPasswordsValid.value && (newPassword.value === newPasswordRetype.value);
}

// Проверки для отключения кнопки перехода между шагами
const isStepPassed = (step: number): boolean => {
  switch (step) {
    case 1: return !isFormValid.value;
    case 2: return !phoneValid.value;
    case 3: return !code.value || code.value.length < 4;
    case 4: return !newPasswordPairValid()
    default: return false;
  }
}

async function executeAction<T>(action: string, payload?: T) {
  inProcess.value = true;
  try {
    await store.dispatch(action, payload);
  } catch (error) {
    console.error(`Произошла ошибка, `, error);
  } finally {
    inProcess.value = false;
    // При успешном изменении пароля показываем модальное окно
    if (step.value === 5) openSuccessModal.value = true
  }
}

const submitStepOne = (payload: StartResetPassword) => {
  executeAction<StartResetPassword>('user/startReset', payload);
}

const submitStepTwo = (phone: string) => {
  const phoneValue = phone.replace(/[^+\d]/g, '');
  executeAction<string>('user/phoneInput', phoneValue);
  startTimer();
};

const submitStepThree = (code: string) => executeAction<string>('user/confirmCode', code);

const submitStepFour = (password: string) => {
  if (props.softReset) {
    executeAction<ResetPassword>('user/resetPass', { newPassword: password, type: 'soft' })
    reachGoal('sidenav-password-change-action')
  } else {
    executeAction<ResetPassword>('user/resetPass', { newPassword: password, type: 'force' })
  }
};

const goHomePage = () => {
  store.commit('user/clearPasswordResetSteps')
  if (props.softReset && localStorage.getItem('token')) {
    userLogout()
  } else {
    router.push('/')
  }
}

const finishPasswordChange = () => {
  openSuccessModal.value = false
  goHomePage()
}

const closeErrorModal = () => {
  store.commit('user/setError', '')
}

watch(step, (newStep) => {
  // Фокус на input во втором шаге
  if (newStep === 2) {
    nextTick(() => {
      if (phoneInput.value) {
        phoneInput.value.focus();
      }
    });
  }
});

onMounted(() => {
  store.commit('user/clearPasswordResetSteps')
  if (props.softReset) {
    store.dispatch('user/updatePasswordResetStep', 4)
  }
})

onBeforeUnmount(() => {
  // Убираем таймер
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_vita_input = _resolveComponent("vita-input")!
  const _component_vita_button = _resolveComponent("vita-button")!
  const _component_vita_modal = _resolveComponent("vita-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (step.value < 4)
        ? (_openBlock(), _createElementBlock("a", {
            key: 0,
            class: _normalizeClass(["dialog__back", {disabled: inProcess.value}]),
            onClick: onBackLinkClicked,
            "data-qa": "restore-dialog-link-back"
          }, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("svg", {
              width: "14",
              height: "20",
              viewBox: "0 0 14 20",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "currentColor"
            }, [
              _createElementVNode("path", { d: "M5.1656e-07 10L11.8005 -9.61441e-08L14 1.86392L4.39899 10L14 18.1361L11.8005 20L5.1656e-07 10Z" })
            ], -1)
          ]))], 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        (step.value === 1)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _cache[12] || (_cache[12] = _createElementVNode("div", { class: "dialog-head" }, [
                _createElementVNode("h3", { class: "dialog__title" }, [
                  _createTextVNode("Восстановление "),
                  _createElementVNode("br"),
                  _createTextVNode(" пароля")
                ]),
                _createElementVNode("p", { class: "dialog__text" }, [
                  _createTextVNode("Введите Ваш личный "),
                  _createElementVNode("br"),
                  _createTextVNode(" рабочий Email и СНИЛС")
                ])
              ], -1)),
              _createElementVNode("form", {
                class: "dialog__form",
                onSubmit: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (submitStepOne({email: email.value, snils: snils.value})), ["prevent"]))
              }, [
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_input, {
                    placeholder: "Введите Email",
                    type: "mail",
                    modelValue: email.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
                    onInput: validateEmail,
                    "floating-label": true,
                    "data-qa": "restore-dialog-input-email"
                  }, null, 8, ["modelValue"]),
                  _withDirectives(_createVNode(_component_vita_input, {
                    placeholder: "Введите СНИЛС",
                    type: "text",
                    onInput: validateSnils,
                    modelValue: snils.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((snils).value = $event)),
                    inputmode: "numeric",
                    "floating-label": true,
                    "data-qa": "restore-dialog-input-snils"
                  }, null, 8, ["modelValue"]), [
                    [_unref(vMaska), '###-###-### ##']
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_button, {
                    disabled: inProcess.value || isStepPassed(step.value),
                    "button-type": 'submit',
                    "data-qa": "restore-dialog-button-next"
                  }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode("Далее", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("a", {
                      class: _normalizeClass({disabled: inProcess.value}),
                      onClick: goHomePage,
                      "data-qa": "restore-dialog-link-home"
                    }, "Главная страница", 2)
                  ])
                ])
              ], 32)
            ], 64))
          : _createCommentVNode("", true),
        (step.value === 2)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[14] || (_cache[14] = _createElementVNode("div", { class: "dialog-head" }, [
                _createElementVNode("h3", { class: "dialog__title" }, [
                  _createTextVNode("Восстановление "),
                  _createElementVNode("br"),
                  _createTextVNode(" пароля")
                ]),
                _createElementVNode("p", { class: "dialog__text" }, "Введите номер телефона для получения кода подтверждения")
              ], -1)),
              _createElementVNode("form", {
                class: "dialog__form",
                onSubmit: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (submitStepTwo(phone.value)), ["prevent"]))
              }, [
                _createElementVNode("div", null, [
                  _withDirectives(_createVNode(_component_vita_input, {
                    placeholder: "Номер телефона",
                    type: "text",
                    modelValue: phone.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((phone).value = $event)),
                    onInput: validatePhone,
                    inputmode: "numeric",
                    "data-maska-reversed": "",
                    "floating-label": true,
                    ref_key: "phoneInput",
                    ref: phoneInput,
                    "data-qa": "restore-dialog-input-phone"
                  }, null, 8, ["modelValue"]), [
                    [_unref(vMaska), '+7 (###) ###-##-##']
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_button, {
                    disabled: inProcess.value || isStepPassed(step.value),
                    "button-type": 'submit',
                    "data-qa": "restore-dialog-button-next"
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode("Далее", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("a", {
                      class: _normalizeClass({disabled: inProcess.value}),
                      onClick: goHomePage,
                      "data-qa": "restore-dialog-link-home"
                    }, "Главная страница", 2)
                  ])
                ])
              ], 32)
            ], 64))
          : _createCommentVNode("", true),
        (step.value === 3)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("div", _hoisted_5, [
                _cache[17] || (_cache[17] = _createElementVNode("h3", { class: "dialog__title" }, [
                  _createTextVNode("Восстановление "),
                  _createElementVNode("br"),
                  _createTextVNode(" пароля")
                ], -1)),
                _createElementVNode("p", _hoisted_6, [
                  _createTextVNode("На номер телефона " + _toDisplayString(phone.value) + " ", 1),
                  _cache[15] || (_cache[15] = _createElementVNode("br", null, null, -1)),
                  _cache[16] || (_cache[16] = _createTextVNode(" отправлен смс-код с подтверждением", -1))
                ])
              ]),
              _createElementVNode("form", {
                class: "dialog__form",
                onSubmit: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (submitStepThree(code.value)), ["prevent"]))
              }, [
                _createElementVNode("div", null, [
                  _cache[19] || (_cache[19] = _createElementVNode("div", { class: "dialog__form-subtitle" }, "Введите код из сообщения", -1)),
                  _createVNode(VitaCodeInput, {
                    onPinCode: handlePinInput,
                    ref_key: "pinInputRef",
                    ref: pinInputRef
                  }, null, 512),
                  _createElementVNode("div", _hoisted_7, [
                    (_unref(timeLeft) > 0)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                          _cache[18] || (_cache[18] = _createTextVNode("Отправить новый код ", -1)),
                          _createElementVNode("span", _hoisted_9, _toDisplayString(formattedTime.value), 1)
                        ]))
                      : (_openBlock(), _createElementBlock("a", {
                          key: 1,
                          class: _normalizeClass(["dialog__form-link", {'disabled': inProcess.value}]),
                          onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (sendNewCode()), ["prevent"])),
                          "data-qa": "restore-dialog-link-resend-code"
                        }, "Отправить новый код", 2))
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_button, {
                    disabled: inProcess.value || isStepPassed(step.value),
                    "button-type": 'submit',
                    "data-qa": "restore-dialog-button-next"
                  }, {
                    default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                      _createTextVNode("Далее", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("a", {
                      class: _normalizeClass({disabled: inProcess.value}),
                      onClick: goHomePage,
                      "data-qa": "restore-dialog-link-home"
                    }, "Главная страница", 2)
                  ])
                ])
              ], 32)
            ], 64))
          : _createCommentVNode("", true),
        (step.value === 4 || step.value === 5)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _cache[22] || (_cache[22] = _createElementVNode("div", { class: "dialog-head" }, [
                _createElementVNode("h3", { class: "dialog__title" }, [
                  _createTextVNode("Придумайте "),
                  _createElementVNode("br"),
                  _createTextVNode(" новый пароль")
                ])
              ], -1)),
              _createElementVNode("form", {
                class: "dialog__form",
                onSubmit: _cache[9] || (_cache[9] = _withModifiers(($event: any) => (submitStepFour(newPassword.value)), ["prevent"])),
                autocomplete: "off"
              }, [
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_input, {
                    placeholder: "Новый пароль",
                    inputName: 'new-password',
                    type: "password",
                    modelValue: newPassword.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((newPassword).value = $event)),
                    "floating-label": true,
                    "data-qa": "restore-dialog-input-new-password"
                  }, null, 8, ["modelValue"]),
                  _createVNode(_component_vita_input, {
                    placeholder: "Повторите пароль",
                    inputName: 'new-password',
                    type: "password",
                    modelValue: newPasswordRetype.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((newPasswordRetype).value = $event)),
                    "floating-label": true,
                    "data-qa": "restore-dialog-input-new-password-retype"
                  }, null, 8, ["modelValue"]),
                  _createVNode(VitaPasswordValidator, {
                    "password-input": newPassword.value,
                    onPasswordValidity: passwordCheck
                  }, null, 8, ["password-input"]),
                  _createElementVNode("div", _hoisted_11, [
                    (newPasswordRetype.value && (newPassword.value !== newPasswordRetype.value))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_12, "Пароли не совпадают"))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_vita_button, {
                    disabled: inProcess.value || isStepPassed(step.value),
                    "button-type": 'submit',
                    "data-qa": "restore-dialog-button-next"
                  }, {
                    default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                      _createTextVNode("Далее", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"]),
                  (!props.softReset)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        _createElementVNode("a", {
                          class: _normalizeClass({disabled: inProcess.value}),
                          onClick: goHomePage,
                          "data-qa": "restore-dialog-link-home"
                        }, "Главная страница", 2)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ], 32)
            ], 64))
          : _createCommentVNode("", true)
      ])
    ]),
    (openSuccessModal.value)
      ? (_openBlock(), _createBlock(_component_vita_modal, {
          key: 0,
          title: modalTitle.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_vita_button, {
              type: "small",
              onClick: finishPasswordChange,
              "data-qa": "restore-dialog-button-success-confirm"
            }, {
              default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                _createTextVNode("Ок", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createBlock(_component_vita_modal, {
          key: 1,
          title: error.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_vita_button, {
              type: "small",
              onClick: closeErrorModal,
              "data-qa": "restore-dialog-button-error-close"
            }, {
              default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                _createTextVNode("Закрыть", -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["title"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})